"use client";

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { User } from "@supabase/supabase-js";
import dayjs from "dayjs";
import { useInterval } from "@/components/common/hooks";
import { validateCommentContent } from "@/components/comments/utils/commentSecurity";
import { logException, SentryFeatureTags } from "@/analytics/sentry";
import { CommentEvents, trackEvent } from "@/analytics";
import { CommentAuthorComedian } from "@punchup/data/models";

export interface Comment {
  id: string;
  content: string;
  user_id: string;
  content_id: string;
  content_type: string;
  created_at: string;
  updated_at: string | null;
  parent_comment_id: string | null;
  clap_count?: number;
  user_clap_count?: number;
  replies?: Comment[];
  is_edited?: boolean;
  users_extra?: {
    id: string;
    username?: string | null;
    phone?: string | null;
  };
  // When set, the comment was authored "as the artist" — render the comedian
  // identity instead of the user. `comedian` is the joined display data.
  author_comedian_id?: string | null;
  comedian?: CommentAuthorComedian | null;
}

interface CommentsResponse {
  comments: Comment[];
  pagination: {
    page: number;
    hasMore: boolean;
    total: number;
  };
}

interface UseCommentsOptions {
  contentId: string;
  currentUser?: User | null;
  enabled?: boolean;
  limit?: number;
}

// Helper function to organize comments into threads
const organizeCommentsIntoThreads = (comments: Comment[]): Comment[] => {
  const commentMap = new Map<string, Comment>();
  const topLevelComments: Comment[] = [];

  // Filter out any invalid comments
  const validComments = comments.filter((comment) => comment && comment.id);

  // First pass: create map and initialize replies arrays
  validComments.forEach((comment) => {
    commentMap.set(comment.id, { ...comment, replies: [] });
  });

  // Second pass: organize into threads
  validComments.forEach((comment) => {
    const commentWithReplies = commentMap.get(comment.id)!;

    if (comment.parent_comment_id) {
      // This is a reply
      const parent = commentMap.get(comment.parent_comment_id);
      if (parent) {
        parent.replies!.push(commentWithReplies);
      }
    } else {
      // This is a top-level comment
      topLevelComments.push(commentWithReplies);
    }
  });

  return topLevelComments;
};

// Fetch comments from API
const fetchComments = async (
  contentId: string,
  page: number = 1,
  limit: number = 20
): Promise<CommentsResponse> => {
  const response = await fetch(
    `/api/comments?content_id=${contentId}&page=${page}&limit=${limit}`
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Failed to fetch comments");
  }

  return response.json();
};

// Edit a comment
const editComment = async ({
  commentId,
  content,
}: {
  commentId: string;
  content: string;
}): Promise<Comment> => {
  const response = await fetch(`/api/comments/${commentId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ content }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Failed to edit comment");
  }

  const data = await response.json();
  return data.comment;
};

// Delete a comment
const deleteComment = async (commentId: string): Promise<void> => {
  const response = await fetch(`/api/comments/${commentId}`, {
    method: "DELETE",
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Failed to delete comment");
  }
};

// Post a new comment
const postComment = async ({
  content,
  contentId,
  contentType = "video",
  parentId,
  authorComedianId,
}: {
  content: string;
  contentId: string;
  contentType?: string;
  parentId?: string;
  authorComedianId?: string;
}): Promise<Comment> => {
  const response = await fetch("/api/comments", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      content,
      content_id: contentId,
      content_type: contentType,
      parent_comment_id: parentId,
      author_comedian_id: authorComedianId,
    }),
  });

  if (response.status === 429) {
    try {
      const errorData = await response.json();
      const retryAfter = errorData.retryAfter || 30;
      throw new Error(
        `Rate limit exceeded. Please wait ${retryAfter} seconds before commenting again.`
      );
    } catch (parseError) {
      throw new Error(
        "Rate limit exceeded. Please wait before commenting again."
      );
    }
  }

  if (!response.ok) {
    try {
      const responseText = await response.text();
      const errorData = JSON.parse(responseText);
      throw new Error(errorData.error || "Failed to post comment");
    } catch (parseError) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
  }

  const data = await response.json();
  return data.comment;
};

// Check for new comments (for polling)
const checkNewComments = async (
  contentId: string,
  afterTimestamp: string,
  excludeUserId?: string
): Promise<{ count: number; hasUserCommented: boolean }> => {
  const response = await fetch(
    `/api/comments/count?content_id=${contentId}&after=${encodeURIComponent(
      afterTimestamp
    )}&exclude_user=${excludeUserId || ""}`
  );

  if (!response.ok) {
    throw new Error("Failed to check for new comments");
  }

  return response.json();
};

/**
 * Custom hook for managing comments with React Query
 * Handles fetching, posting, and polling for new comments
 */
export function useComments({
  contentId,
  currentUser,
  enabled = true,
  limit = 20,
}: UseCommentsOptions) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [allComments, setAllComments] = useState<Comment[]>([]);
  const [lastCommentTimestamp, setLastCommentTimestamp] = useState<
    string | null
  >(null);
  const [newCommentsAvailable, setNewCommentsAvailable] = useState(0);
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);

  // Main query for fetching comments
  const { data, isLoading, error, refetch } = useQuery<CommentsResponse>({
    queryKey: ["comments", contentId, page, limit],
    queryFn: () => fetchComments(contentId, page, limit),
    enabled: enabled && !!contentId,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  // Update allComments when new data is fetched
  useEffect(() => {
    if (data) {
      if (page === 1) {
        // Flatten the API response - extract replies and add them to the flat array
        const flatComments: Comment[] = [];
        data.comments.forEach((comment) => {
          flatComments.push(comment);
          if (comment.replies?.length) {
            flatComments.push(...comment.replies);
          }
        });
        setAllComments(flatComments);

        // Set initial timestamp for polling
        if (data.comments.length > 0 && !lastCommentTimestamp) {
          setLastCommentTimestamp(data.comments[0].created_at);
        }
      } else {
        setAllComments((prev) => [...prev, ...data.comments]);
      }
    }
  }, [data, page, lastCommentTimestamp]);

  // Clear rate limit error after 30 seconds
  useEffect(() => {
    if (rateLimitError) {
      const timer = setTimeout(() => {
        setRateLimitError(null);
      }, 30000);
      return () => clearTimeout(timer);
    }
  }, [rateLimitError]);

  // Poll for new comments every 10 seconds
  const checkForNewComments = async () => {
    if (!enabled || !lastCommentTimestamp) return;

    try {
      const result = await checkNewComments(
        contentId,
        lastCommentTimestamp,
        currentUser?.id
      );
      if (result.count > 0) {
        setNewCommentsAvailable(result.count);
      }
    } catch (error) {
      // Silently handle polling errors to avoid disrupting user experience
      logException(error as Error, {
        tags: {
          feature: SentryFeatureTags.Moderation,
          action: "poll_new_comments",
        },
        level: "warning",
      });
    }
  };

  useInterval(checkForNewComments, enabled ? 10000 : null);

  // Mutation for editing a comment
  const editCommentMutation = useMutation({
    mutationFn: editComment,
    onMutate: async ({ commentId, content }) => {
      // Optimistically update the comment
      setAllComments((prev) =>
        prev.map((c) =>
          c.id === commentId ? { ...c, content, is_edited: true } : c
        )
      );
    },
    onError: (error, variables) => {
      // Revert optimistic update on error
      queryClient.invalidateQueries({
        queryKey: ["comments", contentId],
      });
      logException(error as Error, {
        tags: {
          feature: SentryFeatureTags.Moderation,
          action: "edit_comment",
        },
      });
    },
    onSuccess: (updatedComment) => {
      // Update the comment with the server response
      setAllComments((prev) =>
        prev.map((c) =>
          c.id === updatedComment.id
            ? { ...updatedComment, is_edited: true }
            : c
        )
      );
    },
  });

  // Mutation for deleting a comment
  const deleteCommentMutation = useMutation({
    mutationFn: deleteComment,
    onMutate: async (commentId) => {
      // Optimistically remove the comment
      setAllComments((prev) => prev.filter((c) => c.id !== commentId));
    },
    onError: (error, commentId) => {
      // Revert optimistic update on error
      queryClient.invalidateQueries({
        queryKey: ["comments", contentId],
      });
      logException(error as Error, {
        tags: {
          feature: SentryFeatureTags.Moderation,
          action: "delete_comment",
        },
      });
    },
    onSuccess: () => {
      // Comment already removed optimistically
      // Invalidate queries to ensure consistency
      queryClient.invalidateQueries({
        queryKey: ["comments", contentId],
      });
    },
  });

  // Mutation for posting a new comment
  const postCommentMutation = useMutation({
    mutationFn: async ({
      content,
      parentId,
      authorComedian,
    }: {
      content: string;
      parentId?: string;
      authorComedian?: CommentAuthorComedian | null;
    }) => {
      // Client-side validation first
      const validation = validateCommentContent(content);
      if (!validation.isValid) {
        throw new Error(`Invalid comment: ${validation.errors.join(", ")}`);
      }

      return postComment({
        content: validation.sanitizedContent!,
        contentId,
        parentId,
        authorComedianId: authorComedian?.id,
      });
    },
    onMutate: async ({ content, parentId, authorComedian }) => {
      // Optimistic update: Create temporary comment
      const validation = validateCommentContent(content);
      if (!validation.isValid || !currentUser) {
        return;
      }

      const tempComment: Comment = {
        id: `temp-${dayjs().valueOf()}`,
        content: validation.sanitizedContent!,
        user_id: currentUser.id,
        content_id: contentId,
        content_type: "video",
        created_at: dayjs().toISOString(),
        updated_at: null,
        parent_comment_id: parentId || null,
        author_comedian_id: authorComedian?.id ?? null,
        comedian: authorComedian ?? null,
        users_extra: {
          id: currentUser.id,
          username: currentUser.email?.split("@")[0] || null,
        },
        replies: [],
      };

      // Add optimistic comment to UI
      // Since allComments is a flat array, just add the comment (reply or top-level)
      // The threading will happen automatically in organizeCommentsIntoThreads
      setAllComments((prev) => [tempComment, ...prev]);

      return { tempComment };
    },
    onError: (error, variables, context) => {
      // Remove optimistic comment on error
      if (context?.tempComment) {
        const tempId = context.tempComment.id;
        setAllComments((prev) => prev.filter((c) => c && c.id !== tempId));
      }

      // Handle rate limit errors specially
      if (error.message.includes("Rate limit")) {
        setRateLimitError(error.message);
      } else {
        logException(error as Error, {
          tags: {
            feature: SentryFeatureTags.Moderation,
            action: "post_comment",
          },
        });
      }
    },
    onSuccess: (newComment, variables, context) => {
      // Replace temp comment with real comment
      if (context?.tempComment) {
        const tempId = context.tempComment.id;
        setAllComments((prev) =>
          prev.map((c) => (c.id === tempId ? newComment : c))
        );
      }

      // Update last comment timestamp for new top-level comments
      if (!variables.parentId) {
        setLastCommentTimestamp(newComment.created_at);
      }

      // Analytics: an artist-authored comment was successfully posted. Fired on
      // the mutation success (not submit) so it counts real persistence, and
      // here in the hook because this is the single point that knows the post
      // carried an artist identity regardless of which UI entry point sent it.
      if (variables.authorComedian) {
        trackEvent(CommentEvents.ARTIST_COMMENT_POSTED, {
          contentId,
          comedianId: variables.authorComedian.id,
          commentId: newComment.id,
          isReply: !!variables.parentId,
          parentCommentId: variables.parentId,
        });
      }

      // Invalidate queries to ensure consistency
      queryClient.invalidateQueries({
        queryKey: ["comments", contentId],
      });
    },
  });

  // Function to load more comments
  const loadMore = () => {
    if (data?.pagination.hasMore && !isLoading) {
      setPage((prev) => prev + 1);
    }
  };

  // Function to refresh comments (reset to page 1)
  const refresh = async () => {
    setPage(1);
    setNewCommentsAvailable(0);
    await refetch();
    if (data?.comments.length && data.comments.length > 0) {
      setLastCommentTimestamp(data.comments[0].created_at);
    }
  };

  // Organize comments into threads
  const organizedComments = organizeCommentsIntoThreads(allComments);

  return {
    comments: organizedComments,
    allComments,
    isLoading,
    error,
    pagination: data?.pagination || { page: 1, hasMore: false, total: 0 },
    postComment: postCommentMutation.mutate,
    isPosting: postCommentMutation.isPending,
    postError: postCommentMutation.error,
    editComment: editCommentMutation.mutate,
    isEditing: editCommentMutation.isPending,
    editError: editCommentMutation.error,
    deleteComment: deleteCommentMutation.mutate,
    isDeleting: deleteCommentMutation.isPending,
    deleteError: deleteCommentMutation.error,
    loadMore,
    refresh,
    hasMore: data?.pagination.hasMore || false,
    newCommentsAvailable,
    rateLimitError,
  };
}
